// Vaanmathi
import React, { useState, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Table,
  Form,
  Button,
  Modal,
} from "react-bootstrap";
import {
  FaMoneyBillWave,
  FaClock,
  FaWallet,
  FaBuilding,
  FaCalendarAlt,
  FaEye,
} from "react-icons/fa";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import "jspdf-autotable";
import { useNavigate } from "react-router-dom";
import { FiAlertCircle, FiDownload } from "react-icons/fi";
import { showMyAlert } from "../../Utils/alertService";

function Revenue() {
  const navigate = useNavigate();
  const [salaryData, setSalaryData] = useState([]);
  const [employeeData, setEmployeeData] = useState([]);
  const [filterType, setFilterType] = useState("month");
  const [filterMonth, setFilterMonth] = useState("");
  const [filterPeriod, setFilterPeriod] = useState("");
  const [dateRange, setDateRange] = useState({
    from: "",
    to: "",
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [showDepartmentFilter, setShowDepartmentFilter] = useState(false);
  const [departmentFilter, setDepartmentFilter] = useState("all");
  const [salaryTypeFilter, setSalaryTypeFilter] = useState("all");
  const [initialDataLoaded, setInitialDataLoaded] = useState(false);
  const [departments, setDepartments] = useState([]);
  const [showPdfModal, setShowPdfModal] = useState(false);
  const [currentPdfUrl, setCurrentPdfUrl] = useState(null);
  const [currentPayslip, setCurrentPayslip] = useState(null);
  const [hasFetched, setHasFetched] = useState(false);

  useEffect(() => {
    const checkAuth = () => {
      const userId = sessionStorage.getItem("userMobile");
      if (!userId) {
        navigate("/signIn");
      }
    };
    checkAuth();
  }, [navigate]);

  useEffect(() => {
    const fetchData = async () => {
      if (hasFetched) {
        console.log("Skipping duplicate API call");
        return;
      }

      try {
        setLoading(true);
        const sessionCompanyKey = sessionStorage.getItem("companyKey");
        console.log("Session Company Key:", sessionCompanyKey);

        if (!sessionCompanyKey) {
          throw new Error("Company key not found in session");
        }

        // Fetch salary data
        console.log("Fetching salary data...");
        const salaryResponse = await fetch(
          "https://api.dremerz.net/api/DZ_8924_Payroll_PayrollSalaryCalculation_PayrollAddSalary_PayrollSalaryList"
        );

        if (!salaryResponse.ok) {
          throw new Error(`Salary API failed: ${salaryResponse.status}`);
        }

        const salaryJson = await salaryResponse.json();
        console.log("Raw salary data length:", salaryJson.length);

        const filteredSalaryJson = salaryJson.filter((salary) =>
          salary.companyKey === sessionCompanyKey
        );
        console.log("Filtered salary data:", filteredSalaryJson);
        setSalaryData(filteredSalaryJson);

        // Fetch employee data
        console.log("Fetching employee data...");
        const employeeResponse = await fetch(
          "https://api.dremerz.net/api/DZ_8922_Payroll_PayrollEmployeeBasicDetails_PayrollEmployeeDetails"
        );

        if (!employeeResponse.ok) {
          throw new Error(`Employee API failed: ${employeeResponse.status}`);
        }

        const employeeJson = await employeeResponse.json();
        console.log("Raw employee data length:", employeeJson.length);

        const filteredemployeeJson = employeeJson.filter((emp) =>
          emp.companyKey === sessionCompanyKey
        );
        console.log("Filtered employee data:", filteredemployeeJson);
        setEmployeeData(filteredemployeeJson);

        // Fetch departments data
        console.log("Fetching departments data...");
        const departmentsResponse = await fetch(
          "https://api.dremerz.net/api/DZ_8922_Payroll_PayrollPayslip_PayrollPayslipData_PayrollPayslipLabelsList"
        );

        if (!departmentsResponse.ok) {
          console.warn("Departments API failed, continuing without departments");
        } else {
          const preDepartment = await departmentsResponse.json();
          console.log("Raw departments data:", preDepartment);

          const departmentsJson = preDepartment.filter((data) =>
            data.companyKey === sessionCompanyKey
          );
          console.log("Filtered departments data:", departmentsJson);

          // Extract unique department names - check different possible field names
          const departmentValues = departmentsJson
            .filter((item) => {
              // Check for department in different possible field names
              return (item.sectionName === "Department" || item.label === "Department" || item.fieldName === "department") &&
                (item.value || item.fieldValue);
            })
            .map((item) => item.value || item.fieldValue);

          console.log("Department values extracted:", departmentValues);

          const uniqueDepartments = [...new Set(departmentValues.filter(val => val && val.trim() !== ""))];
          console.log("Unique departments:", uniqueDepartments);
          setDepartments(uniqueDepartments);
        }

        setHasFetched(true);
        setInitialDataLoaded(true);
        setLoading(false);
      } catch (err) {
        console.error("Error fetching data:", err);
        setError(err.message);
        setLoading(false);
      }
    };

    fetchData();
  }, [hasFetched]);

  // Set default filter month after data is loaded
  useEffect(() => {
    if (initialDataLoaded && salaryData.length > 0) {
      console.log("Setting default filter month...");

      // Get all available months from salary data
      const allMonths = salaryData
        .map((item) => {
          if (!item.startDate) return null;
          try {
            const date = new Date(item.startDate);
            if (isNaN(date.getTime())) return null;
            return date.toISOString().slice(0, 7);
          } catch (err) {
            console.error("Date parsing error:", err, "for date:", item.startDate);
            return null;
          }
        })
        .filter((month) => month !== null)
        .sort((a, b) => b.localeCompare(a)); // Newest first

      console.log("Available months from salary data:", allMonths);

      if (allMonths.length > 0) {
        setFilterMonth(allMonths[0]);
        console.log("Set filter month to:", allMonths[0]);
      } else {
        // Fallback to current month
        const currentMonth = new Date().toISOString().slice(0, 7);
        setFilterMonth(currentMonth);
        console.log("Set filter month to current month:", currentMonth);
      }
    }
  }, [initialDataLoaded, salaryData]);

  // Combine salary and employee data
  const combinedData = React.useMemo(() => {
    console.log("=== COMBINING DATA ===");
    console.log("Salary data count:", salaryData.length);
    console.log("Salary data sample:", salaryData[0]);
    console.log("Employee data count:", employeeData.length);
    console.log("Employee data sample:", employeeData[0]);

    if (salaryData.length === 0 || employeeData.length === 0) {
      console.log("No data to combine");
      return [];
    }

    const combined = salaryData.map((salary) => {
      // Find matching employee by userId, employeeId, or other possible ID fields
      const employee = employeeData.find((emp) => {
        return emp.userId === salary.userId ||
          emp.employeeId === salary.employeeId ||
          emp.id === salary.userId ||
          emp.id === salary.employeeId ||
          (emp.employeeId && salary.employeeId && emp.employeeId.toString() === salary.employeeId.toString());
      }) || {};

      const result = { ...salary, ...employee };

      return result;
    });

    console.log("Total combined records:", combined.length);
    if (combined.length > 0) {
      console.log("First combined record:", combined[0]);
      console.log("All keys in first record:", Object.keys(combined[0]));

      // Check for important fields
      console.log("Has startDate?", 'startDate' in combined[0], "Value:", combined[0].startDate);
      console.log("Has normalcalculatedSalary?", 'normalcalculatedSalary' in combined[0], "Value:", combined[0].normalcalculatedSalary);
      console.log("Has overallcalculatedSalary?", 'overallcalculatedSalary' in combined[0], "Value:", combined[0].overallcalculatedSalary);
    }

    return combined;
  }, [salaryData, employeeData]);

  // Get unique months for the dropdown
  const uniqueMonths = React.useMemo(() => {
    if (combinedData.length === 0) {
      console.log("No combined data for unique months");
      return [];
    }

    const months = combinedData
      .map((item) => {
        if (!item.startDate) {
          console.log("Item has no startDate:", item);
          return null;
        }

        try {
          const date = new Date(item.startDate);
          if (isNaN(date.getTime())) {
            console.log("Invalid date:", item.startDate);
            return null;
          }
          const monthStr = date.toISOString().slice(0, 7);
          console.log("Date:", item.startDate, "-> Month:", monthStr);
          return monthStr;
        } catch (err) {
          console.error("Date parsing error:", err, "for date:", item.startDate);
          return null;
        }
      })
      .filter((month) => month !== null);

    const uniqueMonths = [...new Set(months)].sort((a, b) => b.localeCompare(a));
    console.log("Unique months found:", uniqueMonths);
    return uniqueMonths;
  }, [combinedData]);

  // Filter data based on selected filters
  const filteredData = React.useMemo(() => {
    console.log("=== FILTERING DATA ===");
    console.log("Total combined records:", combinedData.length);
    console.log("Filter type:", filterType);
    console.log("Filter month:", filterMonth);
    console.log("Unique months available:", uniqueMonths);

    if (combinedData.length === 0) {
      console.log("No data to filter");
      return [];
    }

    // If no filter is set but we have data, show all data for the default month
    if (filterType === "month" && (!filterMonth || filterMonth.trim() === "")) {
      console.log("No month selected, showing empty");
      return [];
    }

    const result = combinedData.filter((item, index) => {
      console.log(`\nChecking item ${index + 1}:`, item.userName || item.fullName || "Unknown");

      // Check if item has required data
      if (!item.startDate) {
        console.log("  Skipped - no startDate");
        return false;
      }

      // Parse the date
      let itemDate;
      try {
        itemDate = new Date(item.startDate);
        if (isNaN(itemDate.getTime())) {
          console.log("  Skipped - invalid date format:", item.startDate);
          return false;
        }
      } catch (err) {
        console.log("  Skipped - date parsing error:", err);
        return false;
      }

      // Apply date filter
      let dateMatch = false;
      const itemMonth = itemDate.toISOString().slice(0, 7);

      if (filterType === "month" && filterMonth) {
        console.log("  Item month:", itemMonth, "Filter month:", filterMonth);
        dateMatch = itemMonth === filterMonth;
        console.log("  Month match?", dateMatch);
      } else if (filterType === "period" && filterPeriod) {
        const monthsBack = parseInt(filterPeriod);
        const cutoffDate = new Date();
        cutoffDate.setMonth(cutoffDate.getMonth() - monthsBack);
        dateMatch = itemDate >= cutoffDate && itemDate <= new Date();
        console.log("  Period match?", dateMatch);
      } else if (filterType === "range" && dateRange.from && dateRange.to) {
        const fromDate = new Date(dateRange.from);
        const toDate = new Date(dateRange.to);
        dateMatch = itemDate >= fromDate && itemDate <= toDate;
        console.log("  Range match?", dateMatch);
      } else {
        // No date filter selected
        dateMatch = true;
        console.log("  No date filter, showing all");
      }

      // Apply department filter
      const departmentMatch =
        departmentFilter === "all" ||
        !item.department ||
        item.department === departmentFilter;
      console.log("  Department match?", departmentMatch, "Item department:", item.department, "Filter:", departmentFilter);

      // Apply salary type filter
      const salaryTypeMatch =
        salaryTypeFilter === "all" ||
        (salaryTypeFilter === "hourly" && item.salaryType === "hourly") ||
        (salaryTypeFilter === "fixed" && item.salaryType === "fixed");
      console.log("  Salary type match?", salaryTypeMatch, "Item salary type:", item.salaryType, "Filter:", salaryTypeFilter);

      const finalResult = dateMatch && departmentMatch && salaryTypeMatch;
      console.log("  Final result:", finalResult);

      return finalResult;
    });

    console.log("\n=== FILTERING COMPLETE ===");
    console.log("Filtered records count:", result.length);
    console.log("Filtered data:", result);
    return result;
  }, [combinedData, filterType, filterMonth, filterPeriod, dateRange, departmentFilter, salaryTypeFilter, uniqueMonths]);

  // Calculate totals
  const totalNormalSalary = React.useMemo(() => {
    const total = filteredData.reduce(
      (sum, item) => sum + parseFloat(item.normalcalculatedSalary || 0),
      0
    );
    console.log("Total normal salary:", total);
    return total;
  }, [filteredData]);

  const totalOTSalary = React.useMemo(() => {
    const total = filteredData.reduce(
      (sum, item) => sum + parseFloat(item.otcalculatedSalary || 0),
      0
    );
    console.log("Total OT salary:", total);
    return total;
  }, [filteredData]);

  const totalSalary = React.useMemo(() => {
    const total = filteredData.reduce(
      (sum, item) => sum + parseFloat(item.overallcalculatedSalary || 0),
      0
    );
    console.log("Total salary:", total);
    return total;
  }, [filteredData]);

  // Toggle employee selection
  const toggleEmployeeSelection = (employeeId) => {
    setSelectedEmployees((prev) =>
      prev.includes(employeeId)
        ? prev.filter((id) => id !== employeeId)
        : [...prev, employeeId]
    );
  };

  // Select all/none
  const toggleSelectAll = () => {
    if (selectedEmployees.length === filteredData.length) {
      setSelectedEmployees([]);
    } else {
      setSelectedEmployees(filteredData.map((item) => item.userId || item.employeeId || item.id));
    }
  };

  // Handle PDF modal
  const handleOpenPdfModal = (payslip) => {
    console.log("Opening PDF modal for:", payslip);

    // Check for payslip PDF - try different possible field names
    const pdfField = payslip.payslipPdf || payslip.pdfUrl || payslip.documentUrl;

    if (pdfField) {
      setCurrentPayslip(payslip);
      setCurrentPdfUrl(`https://api.dremerz.net/uploads/${pdfField}`);
      setShowPdfModal(true);
    } else {
      console.error("No PDF field found in payslip:", payslip);
      showMyAlert({
        type: "warning",
        title: "No PDF",
        message: "Payslip PDF not available for this record",
        duration: 2500,
      });
    }
  };

  const handleClosePdfModal = () => {
    setShowPdfModal(false);
    setCurrentPdfUrl(null);
    setCurrentPayslip(null);
  };

  const downloadPdf = async () => {
    if (currentPdfUrl && currentPayslip) {
      try {
        const response = await fetch(currentPdfUrl, { mode: "cors" });
        if (!response.ok) throw new Error("Failed to fetch PDF");

        const blob = await response.blob();
        const blobUrl = window.URL.createObjectURL(blob);

        const link = document.createElement("a");
        link.href = blobUrl;
        link.download = `payslip_${currentPayslip.employee_id || currentPayslip.employeeId || currentPayslip.userId}_${currentPayslip.startDate || 'payslip'}.pdf`;

        document.body.appendChild(link);
        link.click();
        link.remove();

        window.URL.revokeObjectURL(blobUrl);
      } catch (err) {
        console.error("Download error:", err);
        showMyAlert({
          type: "danger",
          title: "Failed",
          message: "Failed to download the PDF. Please try again.",
          duration: 2500,
        });
      }
    } else {
      showMyAlert({
        type: "danger",
        title: "Failed",
        message: "Failed to download the PDF. PDF Not available to download",
        duration: 2500,
      });
    }
  };

  // Export to Excel
  const exportToExcel = () => {
    const dataToExport =
      selectedEmployees.length > 0
        ? filteredData.filter((item) => selectedEmployees.includes(item.userId || item.employeeId || item.id))
        : filteredData;

    if (dataToExport.length === 0) {
      showMyAlert({
        type: "warning",
        title: "No Data",
        message: "No Data to Export",
        duration: 2500,
      });
      return;
    }

    const worksheet = XLSX.utils.json_to_sheet(
      dataToExport.map((item) => ({
        "Employee ID": item.employee_id || item.employeeId || "N/A",
        Name: item.userName || item.fullName || "N/A",
        Department: item.department || "N/A",
        "Working Days": item.workingDays || 0,
        "Normal Hours": item.normaltotalWorkingHours || 0,
        "OT Hours": item.ottotalWorkingHours || 0,
        "Normal Salary": parseFloat(item.normalcalculatedSalary || 0).toFixed(2),
        "OT Salary": parseFloat(item.otcalculatedSalary || 0).toFixed(2),
        "Total Salary": parseFloat(item.overallcalculatedSalary || 0).toFixed(2),
        Period: `${item.startDate ? new Date(item.startDate).toLocaleDateString() : "N/A"} - ${item.endDate ? new Date(item.endDate).toLocaleDateString() : "N/A"}`,
        "Salary Type": item.salaryType === "hourly" ? "Hourly" : "Monthly",
      }))
    );

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Payroll Data");

    let fileName = "Payroll_Generated_Payslips";
    if (filterType === "month") {
      fileName += `_${filterMonth}`;
    } else if (filterType === "period") {
      fileName += `_Last_${filterPeriod}_Months`;
    } else {
      fileName += `_${dateRange.from}_to_${dateRange.to}`;
    }

    XLSX.writeFile(workbook, `${fileName}.xlsx`);
  };

  // Export to PDF
  const exportToPDF = () => {
    const dataToExport =
      selectedEmployees.length > 0
        ? filteredData.filter((item) => selectedEmployees.includes(item.userId || item.employeeId || item.id))
        : filteredData;

    if (dataToExport.length === 0) {
      showMyAlert({
        type: "warning",
        title: "No Data",
        message: "No Data to Export",
        duration: 2500,
      });
      return;
    }

    try {
      const doc = new jsPDF();
      doc.setFontSize(16);
      let title = "Payroll Report - ";
      if (filterType === "month") {
        title += new Date(filterMonth).toLocaleString("default", {
          month: "long",
          year: "numeric",
        });
      } else if (filterType === "period") {
        title += `Last ${filterPeriod} Months`;
      } else {
        title += `${new Date(dateRange.from).toLocaleDateString()} to ${new Date(dateRange.to).toLocaleDateString()}`;
      }
      doc.text(title, 14, 15);
      doc.setFontSize(10);
      doc.text(`Total Employees: ${dataToExport.length}`, 14, 25);
      doc.text(`Total Normal Salary: ₹${totalNormalSalary.toFixed(2)}`, 14, 30);
      doc.text(`Total OT Salary: ₹${totalOTSalary.toFixed(2)}`, 14, 35);
      doc.text(`Grand Total: ₹${totalSalary.toFixed(2)}`, 14, 40);

      const tableData = dataToExport.map((item) => [
        item.employee_id || item.employeeId || "N/A",
        item.userName || item.fullName || "N/A",
        item.department || "N/A",
        item.salaryType === "hourly" ? "Hourly" : "Monthly",
        (item.workingDays || 0).toString(),
        (item.normaltotalWorkingHours || 0).toString(),
        (item.ottotalWorkingHours || 0).toString(),
        `₹${parseFloat(item.normalcalculatedSalary || 0).toFixed(2)}`,
        `₹${parseFloat(item.otcalculatedSalary || 0).toFixed(2)}`,
        `₹${parseFloat(item.overallcalculatedSalary || 0).toFixed(2)}`,
        `${item.startDate ? new Date(item.startDate).toLocaleDateString() : "N/A"} - ${item.endDate ? new Date(item.endDate).toLocaleDateString() : "N/A"}`,
      ]);

      doc.autoTable({
        head: [
          [
            "ID",
            "Name",
            "Department",
            "Type",
            "Days",
            "Normal Hrs",
            "OT Hrs",
            "Normal Salary",
            "OT Salary",
            "Total",
            "Period",
          ],
        ],
        body: tableData,
        startY: 45,
        styles: {
          fontSize: 7,
          cellPadding: 1.5,
        },
        headStyles: {
          fillColor: [41, 128, 185],
          textColor: 255,
          fontStyle: "bold",
        },
        alternateRowStyles: {
          fillColor: [240, 240, 240],
        },
        margin: { left: 8, right: 8 },
      });

      let fileName = "Payroll_Report";
      if (filterType === "month") {
        fileName += `_${filterMonth}`;
      } else if (filterType === "period") {
        fileName += `_Last_${filterPeriod}_Months`;
      } else {
        fileName += `_${dateRange.from}_to_${dateRange.to}`;
      }

      doc.save(`${fileName}.pdf`);
    } catch (error) {
      console.error("PDF Generation Error:", error);
      showMyAlert({
        type: "danger",
        title: "Failed",
        message: "Unable to generate PDF Now, please try again later.",
        duration: 2500,
      });
    }
  };

  const isMobile = window.innerWidth < 768;

  if (loading)
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="mt-2">Loading payroll data...</p>
      </div>
    );

  if (error)
    return (
      <div className="text-center py-5 text-danger">
        <div className="alert alert-danger" role="alert">
          Error: {error}
        </div>
        <Button variant="primary" onClick={() => window.location.reload()}>
          Retry
        </Button>
      </div>
    );

  return (
    <Container fluid className="mt-3">
      {/* Header Section */}
      <div className="row align-items-center mb-2 border rounded bg-light py-2 mx-1">
        <div className="col-12 col-md-6 mb-2 mb-md-0">
          <h5 className="mb-0 d-flex align-items-center justify-content-center justify-content-md-start gap-2">
            Revenue
          </h5>
        </div>
        <div className="col-12 col-md-6 d-flex gap-2 justify-content-center justify-content-md-end text-md-end">
          <Button
            variant="outline-success"
            size="sm"
            title="Download Excel"
            onClick={exportToExcel}
            disabled={filteredData.length === 0}
          >
            Export Excel
          </Button>
          <Button
            variant="outline-danger"
            size="sm"
            title="Download PDF"
            onClick={exportToPDF}
            disabled={filteredData.length === 0}
          >
            Export PDF
          </Button>
        </div>
      </div>

      {/* Filters Section */}
      <div className="row align-items-center mb-3 border rounded bg-light py-3 mx-1">
        <div className="col-12">
          <div className="row g-3 align-items-center">
            {/* Salary Type and Time Period Dropdowns */}
            <div className="col-12 col-md-6">
              <div className="d-flex align-items-center gap-3 flex-wrap">
                {/* Salary Type Dropdown */}
                <div className="d-flex align-items-center gap-2">
                  <Form.Select
                    size="sm"
                    value={salaryTypeFilter}
                    onChange={(e) => setSalaryTypeFilter(e.target.value)}
                    style={{ width: "140px" }}
                  >
                    <option value="all">All Types</option>
                    <option value="fixed">Monthly</option>
                    <option value="hourly">Hourly</option>
                  </Form.Select>
                </div>

                {/* Time Period Dropdown */}
                <div className="d-flex align-items-center gap-2">
                  <Form.Select
                    size="sm"
                    value={filterType}
                    onChange={(e) => setFilterType(e.target.value)}
                    style={{ width: "120px" }}
                  >
                    <option value="month">Month</option>
                    <option value="period">Period</option>
                    <option value="range">Range</option>
                  </Form.Select>
                </div>
              </div>
            </div>

            {/* Dynamic Filter Options Based on Selections */}
            <div className="col-12 col-md-6">
              <div className="d-flex align-items-center gap-3 flex-wrap justify-content-md-end">
                {/* Time Period Specific Filters */}
                {filterType === "month" && (
                  <Form.Select
                    size="sm"
                    value={filterMonth}
                    onChange={(e) => setFilterMonth(e.target.value)}
                    style={{ width: "160px" }}
                    disabled={uniqueMonths.length === 0}
                  >
                    {uniqueMonths.length === 0 ? (
                      <option value="">No data available</option>
                    ) : (
                      <>
                        <option value="">Select Month</option>
                        {uniqueMonths.map((month) => {
                          const [year, monthNum] = month.split("-");
                          const monthName = new Date(
                            year,
                            monthNum - 1
                          ).toLocaleString("default", { month: "long" });
                          return (
                            <option key={month} value={month}>
                              {monthName} {year}
                            </option>
                          );
                        })}
                      </>
                    )}
                  </Form.Select>
                )}

                {filterType === "period" && (
                  <Form.Select
                    size="sm"
                    value={filterPeriod}
                    onChange={(e) => setFilterPeriod(e.target.value)}
                    style={{ width: "150px" }}
                  >
                    <option value="">Select Period</option>
                    <option value="3">Last 3 Months</option>
                    <option value="6">Last 6 Months</option>
                    <option value="9">Last 9 Months</option>
                    <option value="12">Last 12 Months</option>
                  </Form.Select>
                )}

                {filterType === "range" && (
                  <div className="d-flex align-items-center gap-2 flex-wrap">
                    <Form.Control
                      size="sm"
                      type="date"
                      value={dateRange.from}
                      onChange={(e) =>
                        setDateRange({ ...dateRange, from: e.target.value })
                      }
                      style={{ width: "130px" }}
                    />
                    <span className="text-muted small">to</span>
                    <Form.Control
                      size="sm"
                      type="date"
                      value={dateRange.to}
                      onChange={(e) =>
                        setDateRange({ ...dateRange, to: e.target.value })
                      }
                      style={{ width: "130px" }}
                      min={dateRange.from}
                    />
                  </div>
                )}

                {/* Department Filter */}
                <div className="d-flex align-items-center gap-2">
                  <Button
                    variant={
                      showDepartmentFilter ? "primary" : "outline-secondary"
                    }
                    onClick={() =>
                      setShowDepartmentFilter(!showDepartmentFilter)
                    }
                    size="sm"
                    disabled={departments.length === 0}
                  >
                    <FaBuilding className="me-1" size={12} />
                    {showDepartmentFilter ? "Hide Dept" : "Department"}
                  </Button>

                  {showDepartmentFilter && (
                    <Form.Select
                      size="sm"
                      value={departmentFilter}
                      onChange={(e) => setDepartmentFilter(e.target.value)}
                      style={{ width: "160px" }}
                    >
                      <option value="all">All Departments</option>
                      {departments.map((dept, index) => (
                        <option key={index} value={dept}>
                          {dept}
                        </option>
                      ))}
                    </Form.Select>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <Row className="mb-4 g-4">
        <Col md={4}>
          <Card className="shadow-sm border-top-md border-top-primary">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-start">
                <div>
                  <h6 className="text-uppercase text-muted fw-semibold mb-2">
                    Normal Salary
                  </h6>
                  <h2 className="mb-0">₹{totalNormalSalary.toFixed(2)}</h2>
                </div>
                <div className="bg-primary bg-opacity-10 p-3 rounded">
                  <FaMoneyBillWave size={24} className="text-primary" />
                </div>
              </div>
              <div className="mt-3">
                <span className="text-success fw-semibold">
                  {filteredData.length} employees
                  {salaryTypeFilter === "hourly" && " (Hourly)"}
                  {salaryTypeFilter === "fixed" && " (Monthly)"}
                </span>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4}>
          <Card className="shadow-sm border-top-md border-top-warning">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-start">
                <div>
                  <h6 className="text-uppercase text-muted fw-semibold mb-2">
                    Overtime
                  </h6>
                  <h2 className="mb-0">₹{totalOTSalary.toFixed(2)}</h2>
                </div>
                <div className="bg-warning bg-opacity-10 p-3 rounded">
                  <FaClock size={24} className="text-warning" />
                </div>
              </div>
              <div className="mt-3">
                <span className="text-success fw-semibold">
                  {filteredData.filter((item) => parseFloat(item.otcalculatedSalary || 0) > 0).length}{" "}
                  employees
                </span>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4}>
          <Card className="shadow-sm border-top-md border-top-success">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-start">
                <div>
                  <h6 className="text-uppercase text-muted fw-semibold mb-2">
                    Total Payroll
                  </h6>
                  <h2 className="mb-0">₹{totalSalary.toFixed(2)}</h2>
                </div>
                <div className="bg-success bg-opacity-10 p-3 rounded">
                  <FaWallet size={24} className="text-success" />
                </div>
              </div>
              <div className="mt-3">
                <span className="text-success fw-semibold">
                  {filterType === "month" && filterMonth
                    ? new Date(filterMonth).toLocaleString("default", {
                      month: "long",
                      year: "numeric",
                    })
                    : filterType === "period" && filterPeriod
                      ? `Last ${filterPeriod} Months`
                      : filterType === "range" && dateRange.from && dateRange.to
                        ? `${new Date(dateRange.from).toLocaleDateString()} - ${new Date(dateRange.to).toLocaleDateString()}`
                        : "Select a filter"}
                </span>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Payroll Details Table */}
      <Card className="shadow-sm mb-4">
        <Card.Header className="bg-white py-3">
          <div className="d-flex justify-content-between align-items-center">
            <h6 className="mb-0">
              Payroll Details {filteredData.length > 0 && `(${filteredData.length} records)`}
            </h6>
            <div>
              <Button
                variant={
                  selectedEmployees.length === filteredData.length && filteredData.length > 0
                    ? "primary"
                    : "outline-primary"
                }
                size="sm"
                className="me-2"
                onClick={toggleSelectAll}
                disabled={filteredData.length === 0}
              >
                {selectedEmployees.length === filteredData.length && filteredData.length > 0
                  ? "Deselect All"
                  : "Select All"}
              </Button>
              <span className="text-muted">
                {selectedEmployees.length > 0
                  ? `${selectedEmployees.length} selected`
                  : ""}
              </span>
            </div>
          </div>
        </Card.Header>
        <Card.Body className="p-0">
          {isMobile ? (
            <div>
              {filteredData.length > 0 ? (
                [...filteredData].reverse().map((item, index) => (
                  <div key={index} className="mb-3">
                    <div
                      className="card shadow-sm"
                      style={{ textAlign: "left", borderRadius: "12px" }}
                    >
                      <div className="card-body">
                        <table className="table table-striped table-bordered mb-2">
                          <tbody>
                            <tr>
                              <td className="fw-bold">Employee ID</td>
                              <td>{item.employee_id || item.employeeId || "N/A"}</td>
                            </tr>
                            <tr>
                              <td className="fw-bold">Name</td>
                              <td>{item.userName || item.fullName || "N/A"}</td>
                            </tr>
                            <tr>
                              <td className="fw-bold">Department</td>
                              <td>{item.department || "N/A"}</td>
                            </tr>
                            <tr>
                              <td className="fw-bold">Salary Type</td>
                              <td>
                                {item.salaryType === "hourly"
                                  ? "Hourly"
                                  : "Monthly"}
                              </td>
                            </tr>
                            <tr>
                              <td className="fw-bold">Normal Salary</td>
                              <td>
                                ₹
                                {parseFloat(
                                  item.normalcalculatedSalary || 0
                                ).toFixed(2)}
                              </td>
                            </tr>
                            <tr>
                              <td className="fw-bold">OT Salary</td>
                              <td>
                                ₹
                                {parseFloat(
                                  item.otcalculatedSalary || 0
                                ).toFixed(2)}
                              </td>
                            </tr>
                            <tr>
                              <td className="fw-bold">Total</td>
                              <td>
                                ₹
                                {parseFloat(
                                  item.overallcalculatedSalary || 0
                                ).toFixed(2)}
                              </td>
                            </tr>
                            <tr>
                              <td className="fw-bold">Period</td>
                              <td>
                                {item.startDate
                                  ? new Date(item.startDate).toLocaleDateString()
                                  : "N/A"}{" "}
                                -{" "}
                                {item.endDate
                                  ? new Date(item.endDate).toLocaleDateString()
                                  : "N/A"}
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-4">
                  <FaCalendarAlt size={48} className="text-muted mb-3" />
                  <h5 className="text-muted">
                    {combinedData.length === 0
                      ? "No payroll data available"
                      : filterType === "month" && (!filterMonth || filterMonth === "")
                        ? "Please select a month from the dropdown"
                        : "No records found for selected filter"}
                  </h5>
                  <p className="text-muted">
                    {combinedData.length === 0
                      ? "Try refreshing the page or check if data exists in the system"
                      : "Try selecting a different month or filter option"}
                  </p>
                </div>
              )}
            </div>
          ) : (
            <div className="table-responsive">
              <Table striped hover responsive bordered className="mb-0">
                <thead  id="text-white-important">
                  <tr id="text-white-important">
                    <th id="text-white-important">
                      <Form.Check
                        type="checkbox"
                        checked={selectedEmployees.length === filteredData.length && filteredData.length > 0}
                        onChange={toggleSelectAll}
                        disabled={filteredData.length === 0}
                      />
                    </th>
                    <th id="text-white-important">Employee ID</th>
                    <th id="text-white-important">Name</th>
                    <th id="text-white-important">Department</th>
                    <th id="text-white-important" className="text-center">Type</th>
                    <th id="text-white-important" className="text-end">Normal Salary</th>
                    <th id="text-white-important" className="text-end">OT Salary</th>
                    <th id="text-white-important" className="text-end">Total</th>
                    <th id="text-white-important">Period</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredData.length > 0 ? (
                    [...filteredData].reverse().map((item, index) => (
                      <tr key={index}>
                        <td>
                          <Form.Check
                            type="checkbox"
                            checked={selectedEmployees.includes(item.userId || item.employeeId || item.id)}
                            onChange={() =>
                              toggleEmployeeSelection(item.userId || item.employeeId || item.id)
                            }
                          />
                        </td>
                        <td>{item.employee_id || item.employeeId || "N/A"}</td>
                        <td className="fw-semibold">
                          {item.userName || item.fullName || "N/A"}
                        </td>
                        <td>
                          {item.department || "N/A"}
                        </td>
                        <td className="text-center">
                          {item.salaryType === "hourly" ? "Hourly" : "Monthly"}
                        </td>
                        <td className="text-end">
                          ₹
                          {parseFloat(item.normalcalculatedSalary || 0).toFixed(2)}
                        </td>
                        <td className="text-end">
                          ₹{parseFloat(item.otcalculatedSalary || 0).toFixed(2)}
                        </td>
                        <td className="text-end fw-bold">
                          ₹
                          {parseFloat(item.overallcalculatedSalary || 0).toFixed(2)}
                        </td>
                        <td>
                          {item.startDate
                            ? new Date(item.startDate).toLocaleDateString()
                            : "N/A"}{" "}
                          -{" "}
                          {item.endDate
                            ? new Date(item.endDate).toLocaleDateString()
                            : "N/A"}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="9" className="text-center py-4">
                        <div className="py-4">
                          <FaCalendarAlt size={48} className="text-muted mb-3" />
                          <h5 className="text-muted">
                            {combinedData.length === 0
                              ? "No payroll data available"
                              : filterType === "month" && (!filterMonth || filterMonth === "")
                                ? "Please select a month from the dropdown"
                                : "No records found for selected filter"}
                          </h5>
                          <p className="text-muted">
                            {combinedData.length === 0
                              ? "Try refreshing the page or check if data exists in the system"
                              : "Try selecting a different month or filter option"}
                          </p>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
                {filteredData.length > 0 && (
                  <tfoot className="table-light">
                    <tr>
                      <td colSpan="5" className="text-end fw-bold">
                        Totals:
                      </td>
                      <td className="text-end fw-bold">
                        ₹{totalNormalSalary.toFixed(2)}
                      </td>
                      <td className="text-end fw-bold">
                        ₹{totalOTSalary.toFixed(2)}
                      </td>
                      <td className="text-end fw-bold text-success">
                        ₹{totalSalary.toFixed(2)}
                      </td>
                      <td></td>
                    </tr>
                  </tfoot>
                )}
              </Table>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* PDF Modal */}
      <Modal
        show={showPdfModal}
        onHide={handleClosePdfModal}
        size="lg"
        centered
        className="pdf-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            Payslip - {currentPayslip?.userName || currentPayslip?.fullName || "Employee"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-0" style={{ height: "75vh" }}>
          {currentPdfUrl ? (
            <iframe
              src={currentPdfUrl}
              title="Payslip PDF"
              width="100%"
              height="100%"
              style={{ border: "none" }}
            />
          ) : (
            <div className="text-center p-5">
              <FiAlertCircle size={48} className="text-danger mb-3" />
              <h5>PDF not available</h5>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleClosePdfModal}>
            Close
          </Button>
          {currentPdfUrl && (
            <Button
              variant="outline-primary"
              onClick={downloadPdf}
              className="d-flex align-items-center gap-1"
            >
              <FiDownload size={16} />
              Download
            </Button>
          )}
        </Modal.Footer>
      </Modal>
    </Container>
  );
}

export default Revenue;